import { ArrowLeft, CheckCircle2, ExternalLink } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { navigate } from "../router";
import { BackLink, Button, ErrorState, Page, type RowState, Spinner, StateLabel } from "../ui";
import { useWs } from "../ws";

type BuyJob = {
	id: number;
	productId: number | null;
	qty: number;
	rail: string | null;
	state: "queued" | "running" | "awaiting_user" | "succeeded" | "failed" | "cancelled";
	checkoutUrl: string | null;
	screenshotPath: string | null;
	screenshotUrl: string | null;
	error: string | null;
	createdAt: number;
	startedAt: number | null;
	updatedAt: number;
};

type Resp = { job?: BuyJob; error?: string };

// Only awaiting_user maps onto an existing RowState by name (needs your
// tap); succeeded/failed borrow live/fault for their obvious good/bad
// meaning, the rest stay idle (no colour).
const STATE_ROW: Record<BuyJob["state"], RowState> = {
	queued: "idle",
	running: "idle",
	awaiting_user: "needs",
	succeeded: "live",
	failed: "fault",
	cancelled: "idle",
};

// Sentence-case for the operator; the raw enum value is an implementation
// detail, not copy.
const STATE_LABEL: Record<BuyJob["state"], string> = {
	queued: "Queued",
	running: "Running",
	awaiting_user: "Waiting for your tap",
	succeeded: "Succeeded",
	failed: "Failed",
	cancelled: "Cancelled",
};

const TERMINAL = new Set(["succeeded", "failed", "cancelled"]);

async function fetchJob(id: string): Promise<Resp> {
	const res = await fetch(`/api/buy/${id}`);
	return (await res.json()) as Resp;
}

export function BuyJobDetail({ id }: { id: string }) {
	const [job, setJob] = useState<BuyJob | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const refresh = useCallback(async () => {
		const r = await fetchJob(id);
		if (r.error) {
			setError(r.error);
			setJob(null);
		} else if (r.job) {
			setJob(r.job);
			setError(null);
		}
	}, [id]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	useWs((msg) => {
		if (msg.type === "buy.update" && String(msg.jobId) === String(id)) {
			refresh();
		}
	});

	const cancel = useCallback(async () => {
		setBusy(true);
		try {
			await fetch(`/api/buy/${id}/cancel`, { method: "POST" });
			await refresh();
		} finally {
			setBusy(false);
		}
	}, [id, refresh]);

	if (error) {
		return (
			<Page title={`Buy job #${id}`} width="narrow" back={<BackLink href="#/">Now</BackLink>}>
				<ErrorState message={error} onRetry={() => void refresh()} />
			</Page>
		);
	}

	if (!job) {
		return (
			<Page width="narrow">
				<div className="flex items-center gap-2 py-6 text-[13px] text-muted">
					<Spinner /> Loading job #{id}…
				</div>
			</Page>
		);
	}

	const banner = renderBanner(job);
	const screenshotSrc = job.screenshotUrl ? `${job.screenshotUrl}?t=${job.updatedAt}` : null;

	return (
		<Page
			title={`Buy job #${job.id}`}
			count={`qty ${job.qty}${job.rail ? ` · rail ${job.rail}` : ""}`}
			back={<BackLink href="#/">Now</BackLink>}
			actions={<StateLabel state={STATE_ROW[job.state]} label={STATE_LABEL[job.state]} />}
			width="wide"
		>
			{/* The checkout screenshot is the thing to look at, so from lg it takes
          the wide column and the facts ride beside it. */}
			<div className={`grid gap-3 ${screenshotSrc ? "lg:grid-cols-[minmax(0,1fr)_320px]" : ""}`}>
				<div className="flex min-w-0 flex-col gap-3 lg:order-2">
					{banner}

					<section className="grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-[10px] border border-hairline bg-paper p-3 text-[13px] text-ink sm:grid-cols-2">
						<Detail label="Product ID" value={String(job.productId ?? "—")} />
						<Detail label="Qty" value={String(job.qty)} />
						<Detail label="Rail" value={job.rail ?? "default"} />
						<Detail label="Created" value={new Date(job.createdAt).toLocaleString()} />
						<Detail label="Started" value={job.startedAt ? new Date(job.startedAt).toLocaleString() : "—"} />
						<Detail label="Updated" value={new Date(job.updatedAt).toLocaleString()} />
						{job.error && (
							<div className="col-span-full flex flex-col gap-0.5">
								<StateLabel state="fault" label="Error" />
								<span className="text-xs break-words text-muted">{job.error}</span>
							</div>
						)}
					</section>

					<div className="flex flex-wrap gap-1.5">
						{!TERMINAL.has(job.state) && (
							<Button variant="secondary" size="sm" onClick={cancel} disabled={busy}>
								Cancel job
							</Button>
						)}
						<Button variant="ghost" size="sm" onClick={() => navigate("/")}>
							<ArrowLeft className="size-3.5" aria-hidden />
							Back to Now
						</Button>
					</div>
				</div>

				{screenshotSrc && (
					<div className="overflow-hidden rounded-[10px] border border-hairline bg-paper lg:order-1">
						<img src={screenshotSrc} alt={`Screenshot for buy job ${job.id}`} className="block w-full" />
					</div>
				)}
			</div>
		</Page>
	);
}

function Detail({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex justify-between gap-4 sm:flex-col sm:items-start sm:gap-0">
			<span className="text-[11px] text-muted">{label}</span>
			<span className="truncate text-ink">{value}</span>
		</div>
	);
}

function renderBanner(job: BuyJob) {
	if (job.state === "queued" || job.state === "running") {
		return (
			<div className="rounded-[10px] border border-hairline bg-paper p-3 text-sm text-ink">
				Worker is driving the checkout. Hang tight…
			</div>
		);
	}
	if (job.state === "awaiting_user" && job.checkoutUrl) {
		return (
			<div className="rounded-[10px] border border-hairline bg-paper p-3">
				<div className="mb-2">
					<StateLabel state="needs" label="Approve in your phone app" />
				</div>
				<p className="mb-3 text-xs text-muted">
					The worker reached the {job.rail ?? "Vipps/Klarna"} hand-off. Open the link below on your phone to approve the
					payment.
				</p>
				<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
					{/* Foil, as every control that completes a purchase is. A
              hand-rolled <a>: it's a real external link (href/target/rel),
              and Button only ever renders a <button> element. */}
					<a
						href={job.checkoutUrl}
						target="_blank"
						rel="noreferrer noopener"
						className="inline-flex min-h-tap items-center justify-center gap-1.5 rounded-md px-3 text-[13px] font-semibold bg-foil hover:brightness-105"
					>
						Open checkout <ExternalLink className="size-4" aria-hidden />
					</a>
					<Button variant="secondary" onClick={() => copy(job.checkoutUrl!)}>
						Copy URL
					</Button>
				</div>
			</div>
		);
	}
	if (job.state === "succeeded") {
		return (
			<div className="flex items-start gap-3 rounded-[10px] border border-hairline bg-paper p-3 text-sm text-ink">
				<CheckCircle2 className="mt-0.5 size-5 shrink-0 text-ink" />
				<span>Order placed. Check your store account for the confirmation email.</span>
			</div>
		);
	}
	if (job.state === "failed") {
		return <ErrorState message="Buy failed — see error below; screenshot is the last frame the worker captured." />;
	}
	if (job.state === "cancelled") {
		return <div className="rounded-[10px] border border-hairline bg-paper p-3 text-sm text-ink">Cancelled.</div>;
	}
	return null;
}

function copy(text: string) {
	if (navigator.clipboard) {
		void navigator.clipboard.writeText(text);
	}
}
